import { Alert, Button, Checkbox, Group, SegmentedControl, Stack, Table, Tabs, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconAlertCircle, IconFileTypePdf, IconMessage } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { odpriPdf } from '../api/pdf';
import { sporocilo } from '../api/napaka';
import { denar, obdobje } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { NovaPovratnaInformacija } from '../povratne/NovaPovratnaInformacija';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { barve } from '../tema';

const ZAVIHKI = [
  { kljuc: 'obdobje', naziv: 'Obdobje' },
  { kljuc: 'znamke', naziv: 'Po znamkah' },
  { kljuc: 'porocila', naziv: 'Poročila' },
  { kljuc: 'predlog', naziv: 'Predlog' },
];

/** Analize: zavihki Obdobje, Po znamkah, Poročila (PDF s strežnika) in Predlog. */
export function Analize() {
  const [parametri, setParametri] = useSearchParams();
  const zavihek = ZAVIHKI.some((z) => z.kljuc === parametri.get('zavihek')) ? parametri.get('zavihek')! : 'obdobje';
  return (
    <ZaslonSeznama>
      <GlavaZaslona naslov="Analize" opis="Prodaja po obdobjih in znamkah ter poročila." />
      <Tabs
        value={zavihek}
        onChange={(z) => setParametri(z && z !== 'obdobje' ? { zavihek: z } : {}, { replace: true })}
        keepMounted={false}
      >
        <Tabs.List mb="md" style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
          {ZAVIHKI.map((z) => (
            <Tabs.Tab key={z.kljuc} value={z.kljuc} style={{ whiteSpace: 'nowrap' }}>
              {z.naziv}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel value="obdobje">
          <Obdobje />
        </Tabs.Panel>
        <Tabs.Panel value="znamke">
          <PoZnamkah />
        </Tabs.Panel>
        <Tabs.Panel value="porocila">
          <Porocila />
        </Tabs.Panel>
        <Tabs.Panel value="predlog">
          <Predlog />
        </Tabs.Panel>
      </Tabs>
    </ZaslonSeznama>
  );
}

function Tabela({ glava, children, minSirina }: { glava: string[]; children: ReactNode; minSirina: number }) {
  return (
    <Table.ScrollContainer minWidth={minSirina}>
      <Table striped highlightOnHover className="stevilke" verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            {glava.map((g, i) => (
              <Table.Th key={g} ta={i === 0 ? undefined : 'right'} style={{ whiteSpace: 'nowrap' }}>
                <Text component="span" fw={700} size="11px" c={barve.besediloSekundarno}>
                  {g.toUpperCase()}
                </Text>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{children}</Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** Prodaja po obdobjih (letno/mesečno/dnevno); razliko izračuna strežnik. */
function Obdobje() {
  const [natancnost, setNatancnost] = useState('2');
  const poizvedba = useQuery({
    queryKey: ['analize-mesecno', natancnost],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/analize/mesecno', { signal, params: { query: { natancnost: Number(natancnost) } } })),
    placeholderData: keepPreviousData,
  });
  return (
    <Kartica naslov="Prodaja po obdobjih">
      <SegmentedControl
        value={natancnost}
        onChange={setNatancnost}
        data={[
          { value: '1', label: 'Letno' },
          { value: '2', label: 'Mesečno' },
          { value: '3', label: 'Dnevno' },
        ]}
        mb="xs"
      />
      <Text size="sm" c="dimmed" mb="md">
        Zneski so z DDV. Nakup vozil je nakupna cena po datumu nakupa, dodatni stroški so v svojem stolpcu.
      </Text>
      {!poizvedba.data ? (
        <StanjeNalaganja
          napaka={poizvedba.error}
          ponovi={() => void poizvedba.refetch()}
          naslov="Analize ni bilo mogoče naložiti"
        />
      ) : poizvedba.data.length === 0 ? (
        <Text c="dimmed" size="sm">
          Ni podatkov.
        </Text>
      ) : (
        <Tabela
          minSirina={820}
          glava={[
            'Obdobje',
            'Nakup vozil',
            'Stroški vozil',
            'Prodana vozila',
            'Storitve',
            'Posredniška prodaja',
            'Razlika',
          ]}
        >
          {poizvedba.data.map((m) => (
            <Table.Tr key={m.interval}>
              <Table.Td className="brez-preloma">{obdobje(m.interval)}</Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(m.odhodkiNakupVozil)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(m.odhodkiStroskiVozil)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(m.prihodkiProdaja)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(m.prihodkiStoritve)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(m.posredniskiDohodek)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma" fw={600} c={(m.razlika ?? 0) < 0 ? 'red' : undefined}>
                {denar(m.razlika)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Tabela>
      )}
    </Kartica>
  );
}

/** Prodaja po znamkah (vsa / brez posredniške / samo posredniška). */
function PoZnamkah() {
  const [posredniske, setPosredniske] = useState('vse');
  const poizvedba = useQuery({
    queryKey: ['analize-znamke', posredniske],
    queryFn: ({ signal }) => podatki(api.GET('/api/analize/znamke', { signal, params: { query: { posredniske } } })),
    placeholderData: keepPreviousData,
  });
  return (
    <Kartica naslov="Prodaja po znamkah">
      <SegmentedControl
        value={posredniske}
        onChange={setPosredniske}
        data={[
          { value: 'vse', label: 'Vsa prodana vozila' },
          { value: 'brez', label: 'Brez posredniške' },
          { value: 'samo', label: 'Samo posredniška' },
        ]}
        orientation="horizontal"
        fullWidth
        mb="md"
        style={{ maxWidth: 620 }}
      />
      {!poizvedba.data ? (
        <StanjeNalaganja
          napaka={poizvedba.error}
          ponovi={() => void poizvedba.refetch()}
          naslov="Analize ni bilo mogoče naložiti"
        />
      ) : poizvedba.data.length === 0 ? (
        <Text c="dimmed" size="sm">
          Ni prodanih vozil.
        </Text>
      ) : (
        <Tabela
          minSirina={760}
          glava={['Znamka', 'Št. prodanih', 'Povprečje', 'Skupaj', 'Najmanj', 'Največ', 'Dodatni str.']}
        >
          {poizvedba.data.map((z) => (
            <Table.Tr key={z.znamka}>
              <Table.Td>{z.znamka}</Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {z.steviloProdanih}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(z.povprecje)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(z.vsota)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(z.najmanjsiPlus)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(z.najvecjiPlus)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(z.dodatniStroski)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Tabela>
      )}
    </Kartica>
  );
}

const danes = () => new Date().toLocaleDateString('sv-SE');
const prviVMesecu = () => danes().slice(0, 8) + '01';

/** Gumb za PDF poročilo: odpre PDF, razlog ali napako pokaže ob gumbu (dejanje nikoli tiho ne odpove). */
function GumbPdf({ naziv, razlog, odpri }: { naziv: string; razlog: string | null; odpri: () => Promise<void> }) {
  const [napaka, setNapaka] = useState<string | null>(null);
  const [delam, setDelam] = useState(false);
  const klik = async () => {
    setNapaka(null);
    if (razlog) {
      setNapaka(razlog);
      return;
    }
    setDelam(true);
    try {
      await odpri();
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDelam(false);
    }
  };
  return (
    <Stack gap="xs" mt="md">
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert" py={8}>
          {napaka}
        </Alert>
      )}
      <Group>
        <Button leftSection={<IconFileTypePdf size={18} />} onClick={() => void klik()} loading={delam}>
          {naziv}
        </Button>
      </Group>
    </Stack>
  );
}

/** Poročila v PDF (izdela strežnik). */
function Porocila() {
  const [od, setOd] = useState<string | null>(prviVMesecu());
  const [doDatuma, setDo] = useState<string | null>(danes());
  const [prodajalci, setProdajalci] = useState(false);
  const [kupci, setKupci] = useState(false);
  const [dan, setDan] = useState<string | null>(danes());
  const [vsa, setVsa] = useState(true);
  const [posredniska, setPosredniska] = useState(true);
  const [lastna, setLastna] = useState(true);
  const datumi = { valueFormat: 'D. M. YYYY', w: { base: '100%', xs: 180 } } as const;

  return (
    <Stack gap="md">
      <Kartica naslov="Izvoz statistike za obdobje">
        <Group gap="sm" align="flex-end">
          <DatePickerInput label="Začetek" value={od} onChange={setOd} {...datumi} />
          <DatePickerInput label="Konec" value={doDatuma} onChange={setDo} {...datumi} />
        </Group>
        <Stack gap="xs" mt="md">
          <Checkbox
            label="Dodaj naslove prodajalcev"
            checked={prodajalci}
            onChange={(e) => setProdajalci(e.currentTarget.checked)}
          />
          <Checkbox label="Dodaj naslove kupcev" checked={kupci} onChange={(e) => setKupci(e.currentTarget.checked)} />
        </Stack>
        <GumbPdf
          naziv="Izvozi statistiko"
          razlog={!od || !doDatuma ? 'Izberite začetek in konec obdobja.' : null}
          odpri={() =>
            odpriPdf(
              api.GET('/api/porocila/obdobje/pdf', {
                params: { query: { od: od!, do: doDatuma!, prodajalci, kupci } },
                parseAs: 'blob',
              }),
            )
          }
        />
      </Kartica>
      <Kartica naslov="Izpis zaloge na določen dan">
        <DatePickerInput label="Datum" value={dan} onChange={setDan} {...datumi} />
        <Stack gap="xs" mt="md">
          <Checkbox label="Vsa vozila" checked={vsa} onChange={(e) => setVsa(e.currentTarget.checked)} />
          <Checkbox
            label="Posredniška vozila"
            checked={posredniska}
            onChange={(e) => setPosredniska(e.currentTarget.checked)}
          />
          <Checkbox label="Lastna vozila" checked={lastna} onChange={(e) => setLastna(e.currentTarget.checked)} />
        </Stack>
        <GumbPdf
          naziv="Izvozi zalogo"
          razlog={
            !dan ? 'Izberite datum.' : !vsa && !posredniska && !lastna ? 'Izberite vsaj eno skupino vozil.' : null
          }
          odpri={() =>
            odpriPdf(
              api.GET('/api/porocila/zaloga/pdf', {
                params: { query: { dan: dan!, vsa, posredniska, lastna } },
                parseAs: 'blob',
              }),
            )
          }
        />
      </Kartica>
    </Stack>
  );
}

function Predlog() {
  const [odprto, setOdprto] = useState(false);
  return (
    <Kartica naslov="Predlog za analizo">
      <Text size="sm" c="dimmed">
        Če imate idejo ali predlog za analizo, nam jo pošljite in vas bomo kontaktirali.
      </Text>
      <Button mt="md" variant="light" leftSection={<IconMessage size={18} />} onClick={() => setOdprto(true)}>
        Povratne informacije
      </Button>
      <NovaPovratnaInformacija odprto={odprto} zapri={() => setOdprto(false)} />
    </Kartica>
  );
}
